import { useEffect, useState } from 'react';
import { Alert, Button, Collapse, Modal, Progress, Select, Space, Tag, Typography } from 'antd';
import type { HermesEstimationMode } from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import { bulkEstimate, getJob, cancelJob } from './api.ts';
import { useJobs } from '../jobs/JobsContext.tsx';

const MODE_OPTIONS: Array<{ value: HermesEstimationMode; label: string }> = [
  { value: 'auto', label: 'Авто' },
  { value: 'high_level', label: 'Высокоуровневая' },
  { value: 'detailed', label: 'Детальная' },
];

interface EstimateInboxModalProps {
  open: boolean;
  spaceId: string;
  itemIds: string[];
  onClose: () => void;
  onApplied?: () => void;
}

type Phase = 'idle' | 'running' | 'success' | 'error';

interface BulkResult {
  started: number;
  failedToStart: number;
  results?: Array<{ itemId: string; status: string; error?: string }>;
}

const POLL_MS = 2000;

export function EstimateInboxModal({
  open,
  spaceId,
  itemIds,
  onClose,
  onApplied,
}: EstimateInboxModalProps): JSX.Element {
  const [phase, setPhase] = useState<Phase>('idle');
  const [jobId, setJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: itemIds.length });
  const [result, setResult] = useState<BulkResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<HermesEstimationMode>('auto');
  const { registerJob } = useJobs();

  // Сброс при каждом открытии.
  useEffect(() => {
    if (open) {
      setPhase('idle');
      setJobId(null);
      setProgress({ done: 0, total: itemIds.length });
      setResult(null);
      setError(null);
    }
  }, [open, itemIds.length]);

  useEffect(() => {
    if (!jobId || phase !== 'running') return;
    let active = true;
    const interval = setInterval(async () => {
      if (!active) return;
      try {
        const job = await getJob(jobId);
        if (!active) return;
        setProgress({ done: job.progress ?? 0, total: job.total ?? itemIds.length });
        if (job.status === 'success') {
          clearInterval(interval);
          setResult((job.result ?? { started: 0, failedToStart: 0 }) as BulkResult);
          setPhase('success');
          onApplied?.();
        } else if (job.status === 'error' || job.status === 'cancelled') {
          clearInterval(interval);
          setError(job.error ?? 'Оценка не выполнена');
          setPhase('error');
        }
      } catch {
        /* транзиентная ошибка опроса */
      }
    }, POLL_MS);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [jobId, phase, itemIds.length, onApplied]);

  const handleCancel = async () => {
    if (!jobId) return;
    try {
      await cancelJob(jobId);
      setPhase('error');
      setError('Оценка прервана пользователем');
    } catch {
      /* job уже завершился — следующий тик поллинга подхватит финальный статус */
    }
  };

  const handleStart = async () => {
    setError(null);
    setPhase('running');
    try {
      const { jobId: id } = await bulkEstimate(spaceId, { itemIds, estimationMode: mode });
      setJobId(id);
      registerJob(id, `Оценка Hermes · ${itemIds.length} записей`);
    } catch (err) {
      setPhase('error');
      setError(err instanceof ApiError ? err.message : 'Не удалось запустить оценку');
    }
  };

  const percent =
    progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  const failedDetails = (result?.results ?? []).filter((r) => r.status === 'error');

  return (
    <Modal
      open={open}
      title="Групповая оценка трудоёмкости"
      onCancel={onClose}
      onOk={phase === 'idle' ? handleStart : onClose}
      okText={phase === 'idle' ? `Оценить ${itemIds.length}` : 'Закрыть'}
      cancelButtonProps={{ style: phase === 'success' ? { display: 'none' } : undefined }}
      confirmLoading={phase === 'running'}
      maskClosable={phase !== 'running'}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {phase === 'idle' && (
          <>
            <Typography.Text>
              Будет запущена оценка для записей: <b>{itemIds.length}</b>. Оценка
              считается внешней системой Hermes в фоне; результат появится в полях и
              в колонке «Оценка» по мере готовности.
            </Typography.Text>
            <Space>
              <Typography.Text type="secondary">Режим оценки:</Typography.Text>
              <Select value={mode} onChange={setMode} options={MODE_OPTIONS} style={{ minWidth: 170 }} />
            </Space>
          </>
        )}

        {(phase === 'running' || phase === 'success') && (
          <Progress
            percent={percent}
            status={phase === 'success' ? 'success' : 'active'}
          />
        )}

        {phase === 'running' && (
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Typography.Text type="secondary">
              Обработано {progress.done} из {progress.total}…
            </Typography.Text>
            <Button size="small" danger onClick={handleCancel}>
              Прервать
            </Button>
          </Space>
        )}

        {phase === 'success' && result && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Text>
              Запущено: <Tag color="success">{result.started}</Tag>
              {result.failedToStart > 0 && <Tag color="error">{result.failedToStart} не стартовали</Tag>}
              <Typography.Text type="secondary"> — идёт оценка в фоне</Typography.Text>
            </Typography.Text>
            {failedDetails.length > 0 && (
              <Collapse
                items={[
                  {
                    key: 'errors',
                    label: <Typography.Text type="danger">Ошибки ({failedDetails.length})</Typography.Text>,
                    children: (
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {failedDetails.map((e) => (
                          <li key={e.itemId}>
                            <Typography.Text type="secondary">{e.itemId}</Typography.Text>:{' '}
                            <Typography.Text type="danger">{e.error ?? e.status}</Typography.Text>
                          </li>
                        ))}
                      </ul>
                    ),
                  },
                ]}
              />
            )}
          </Space>
        )}

        {error && <Alert type="error" showIcon message={error} />}
      </Space>
    </Modal>
  );
}
